import React from 'react';
import {
  Box,
  Typography,
  Rating,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormLabel,
  Slider,
  Chip
} from '@mui/material';
import {
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  Star,
  StarBorder,
  ThumbUp,
  ThumbDown,
  CheckCircle,
  Cancel
} from '@mui/icons-material';

// Star Rating Component
interface StarRatingProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  max?: number;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  precision?: number;
  showValue?: boolean;
}

export const StarRating: React.FC<StarRatingProps> = ({
  label,
  value,
  onChange,
  max = 5,
  size = 'medium',
  disabled = false,
  precision = 1,
  showValue = true
}) => {
  return (
    <FormControl component="fieldset">
      {label && (
        <FormLabel component="legend" sx={{ mb: 1 }}>
          {label}
        </FormLabel>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Rating
          value={value}
          onChange={(_, newValue) => onChange(newValue || 0)}
          max={max}
          size={size}
          disabled={disabled}
          precision={precision}
          icon={<Star fontSize="inherit" />}
          emptyIcon={<StarBorder fontSize="inherit" />}
          sx={{
            '& .MuiRating-iconFilled': {
              color: '#F8B133'
            },
            '& .MuiRating-iconHover': {
              color: '#f29100'
            }
          }}
        />
        {showValue && (
          <Typography variant="body2" color="text.secondary">
            {value}/{max}
          </Typography>
        )}
      </Box>
    </FormControl>
  );
};

// Smile Rating Component (Emoji-based rating)
interface SmileRatingProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

export const SmileRating: React.FC<SmileRatingProps> = ({
  label,
  value,
  onChange,
  size = 'medium',
  disabled = false
}) => {
  const customIcons: { [index: string]: { icon: React.ReactElement; label: string } } = {
    1: {
      icon: <SentimentVeryDissatisfied color="error" />,
      label: 'Very bad',
    },
    2: {
      icon: <SentimentDissatisfied color="error" />,
      label: 'Bad',
    },
    3: {
      icon: <SentimentNeutral color="warning" />,
      label: 'Okay',
    },
    4: {
      icon: <SentimentSatisfied color="success" />,
      label: 'Good',
    },
    5: {
      icon: <SentimentVerySatisfied color="success" />,
      label: 'Very good',
    },
  };

  function IconContainer(props: any) {
    const { value, ...other } = props;
    return <span {...other}>{customIcons[value].icon}</span>;
  }

  return (
    <FormControl component="fieldset">
      {label && (
        <FormLabel component="legend" sx={{ mb: 1 }}>
          {label}
        </FormLabel>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Rating
          value={value}
          onChange={(_, newValue) => onChange(newValue || 0)}
          max={5}
          size={size}
          disabled={disabled}
          IconContainerComponent={IconContainer}
          getLabelText={(value: number) => customIcons[value]?.label || ''}
          highlightSelectedOnly
        />
        {value > 0 && (
          <Typography variant="body2" color="text.secondary">
            {customIcons[value]?.label}
          </Typography>
        )}
      </Box>
    </FormControl>
  );
};

// Yes/No Rating Component - SmartNyuki Style
interface YesNoRatingProps {
  label?: string;
  value: any; // Can be boolean, number (0/1), or null
  onChange: (value: number | null) => void;
  disabled?: boolean;
  showLabels?: boolean;
  yesRed?: boolean; // For cases where YES should be red (e.g., disease present)
  size?: 'small' | 'large';
}

export const YesNoRating: React.FC<YesNoRatingProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  showLabels = true,
  yesRed = false,
  size = 'small',
}) => {
  const isYes = value === 1 || value === true;
  const isNo = value === 0 || value === false;

  const handleClick = (newValue: number) => {
    // Toggle if clicking the same value
    if ((newValue === 1 && isYes) || (newValue === 0 && isNo)) {
      onChange(null);
    } else {
      onChange(newValue);
    }
  };

  return (
    <FormControl component="fieldset">
      {label && (
        <FormLabel component="legend" sx={{ mb: 1 }}>
          {label}
        </FormLabel>
      )}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <ToggleButton
          value="yes"
          selected={isYes}
          onChange={() => handleClick(1)}
          disabled={disabled}
          sx={{
            px: size === 'large' ? 2 : 1.5,
            py: size === 'large' ? 1 : 0.5,
            minWidth: size === 'large' ? 88 : undefined,
            height: size === 'large' ? 44 : 32,
            border: 'none',
            textTransform: 'none',
            fontSize: size === 'large' ? '0.95rem' : '0.875rem',
            fontWeight: isYes ? 600 : 400,
            color: isYes
              ? (yesRed ? 'error.main' : 'success.main')
              : 'text.secondary',
            '&.Mui-selected': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
            '&:hover': {
              backgroundColor: 'action.hover',
              border: 'none',
            },
          }}
        >
          <CheckCircle
            sx={{
              fontSize: '1.2rem',
              mr: showLabels ? 0.5 : 0,
              color: isYes
                ? (yesRed ? 'error.main' : 'success.main')
                : 'text.disabled',
            }}
          />
          {showLabels && 'YES'}
        </ToggleButton>

        <ToggleButton
          value="no"
          selected={isNo}
          onChange={() => handleClick(0)}
          disabled={disabled}
          sx={{
            px: size === 'large' ? 2 : 1.5,
            py: size === 'large' ? 1 : 0.5,
            minWidth: size === 'large' ? 88 : undefined,
            height: size === 'large' ? 44 : 32,
            border: 'none',
            textTransform: 'none',
            fontSize: size === 'large' ? '0.95rem' : '0.875rem',
            fontWeight: isNo ? 600 : 400,
            color: isNo
              ? (yesRed ? 'success.main' : 'error.main')
              : 'text.secondary',
            '&.Mui-selected': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
            '&:hover': {
              backgroundColor: 'action.hover',
              border: 'none',
            },
          }}
        >
          <Cancel
            sx={{
              fontSize: '1.2rem',
              mr: showLabels ? 0.5 : 0,
              color: isNo
                ? (yesRed ? 'success.main' : 'error.main')
                : 'text.disabled',
            }}
          />
          {showLabels && 'NO'}
        </ToggleButton>
      </Box>
    </FormControl>
  );
};

// Slider Rating Component
interface SliderRatingProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  marks?: boolean | Array<{ value: number; label?: string }>;
  disabled?: boolean;
  showValue?: boolean;
  valueLabelDisplay?: 'on' | 'auto' | 'off';
  color?: 'primary' | 'secondary';
}

export const SliderRating: React.FC<SliderRatingProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 10,
  step = 1,
  marks = true,
  disabled = false,
  showValue = true,
  valueLabelDisplay = 'auto',
  color = 'primary'
}) => {
  const defaultMarks = marks === true
    ? Array.from({ length: (max - min) / step + 1 }, (_, i) => ({
      value: min + i * step,
      label: (min + i * step).toString()
    }))
    : marks;

  return (
    <FormControl component="fieldset" sx={{ width: '100%' }}>
      {label && (
        <FormLabel component="legend" sx={{ mb: 2 }}>
          {label}
        </FormLabel>
      )}
      <Box sx={{ px: 2 }}>
        <Slider
          value={value}
          onChange={(_, newValue) => onChange(newValue as number)}
          min={min}
          max={max}
          step={step}
          marks={defaultMarks}
          disabled={disabled}
          valueLabelDisplay={valueLabelDisplay}
          sx={{
            color: color === 'primary' ? '#F8B133' : undefined,
            '& .MuiSlider-thumb': {
              backgroundColor: '#F8B133',
              '&:hover': {
                boxShadow: '0px 0px 0px 8px rgba(248, 177, 51, 0.16)'
              }
            },
            '& .MuiSlider-track': {
              backgroundColor: '#F8B133'
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#ddd'
            },
            '& .MuiSlider-mark': {
              backgroundColor: '#F8B133'
            },
            '& .MuiSlider-markActive': {
              backgroundColor: '#f29100'
            }
          }}
        />
        {showValue && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Chip
              label={`${value}/${max}`}
              size="small"
              sx={{
                backgroundColor: '#F8B133',
                color: 'white'
              }}
            />
          </Box>
        )}
      </Box>
    </FormControl>
  );
};

// Condition Rating Component (specific for hive conditions)
interface ConditionRatingProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export const ConditionRating: React.FC<ConditionRatingProps> = ({
  label = 'Overall Condition',
  value,
  onChange,
  disabled = false
}) => {
  const conditions = [
    { value: 1, label: 'Critical', color: '#f44336' },
    { value: 2, label: 'Poor', color: '#ff9800' },
    { value: 3, label: 'Fair', color: '#ffeb3b' },
    { value: 4, label: 'Good', color: '#8bc34a' },
    { value: 5, label: 'Excellent', color: '#4caf50' }
  ];

  return (
    <FormControl component="fieldset">
      <FormLabel component="legend" sx={{ mb: 1 }}>
        {label}
      </FormLabel>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {conditions.map((condition) => (
          <Chip
            key={condition.value}
            label={condition.label}
            onClick={() => !disabled && onChange(condition.value)}
            variant={value === condition.value ? 'filled' : 'outlined'}
            sx={{
              backgroundColor: value === condition.value ? condition.color : 'transparent',
              borderColor: condition.color,
              color: value === condition.value ? 'white' : condition.color,
              cursor: disabled ? 'default' : 'pointer',
              '&:hover': disabled ? {} : {
                backgroundColor: condition.color,
                color: 'white'
              }
            }}
            disabled={disabled}
          />
        ))}
      </Box>
      {value > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Selected: {conditions.find(c => c.value === value)?.label}
        </Typography>
      )}
    </FormControl>
  );
};
